<template>
  <table v-if="store.alerts.length" class="alerts-table">
    <thead>
      <tr>
        <th class="name">Nom</th>
        <th>Veille</th>
        <th>Corpus</th>
        <th class="center">Canal</th>
        <th class="center state">Active</th>
        <th class="center actions">Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="alert in store.alerts" :key="alert._id" class="alert-row">
        <td>
          {{ alert.name }}
        </td>
        <td>
          {{ alert.dashboard_name }}
        </td>
        <td>
          <div class="services">
            <ServiceTooltip
              v-for="(service, index) in services"
              :key="index"
              :text="service.displayName"
            >
              <span
                class="service-icon"
                :class="
                  alert.alert_services.includes(service.name) ? 'active' : ''
                "
              >
                <i class="fa-lg" :class="service.icon"></i>
              </span>
            </ServiceTooltip>
          </div>
        </td>
        <td>
          <div class="center notification-types">
            <i
              v-if="alert.notification_type === 'email'"
              class="fa-solid fa-lg fa-envelope"
              title="Email"
            ></i>
            <i
              v-if="alert.notification_type === 'telegram'"
              class="fa-brands fa-lg fa-telegram"
              title="Telegram"
            ></i>
            <i
              v-if="alert.notification_type === 'slack'"
              class="fa-brands fa-lg fa-slack"
              title="Slack"
            ></i>
            <i
              v-if="alert.notification_type === 'msteams'"
              class="fa-kit fa-lg fa-ms-teams"
              title="Microsoft Teams"
            ></i>
            <i
              v-if="alert.notification_type === 'default'"
              class="fa-solid fa-crown"
              title="Canal de préférence"
            ></i>
          </div>
        </td>
        <td>
          <div class="center">
            <SwitchSlide
              :is-active="alert.active"
              @toggle="store.toggleAlert(alert._id)"
              @click="handleOnClick"
            />
          </div>
        </td>
        <td>
          <div class="center actions">
            <ButtonWithPaywall
              rounded
              :overrideIcon="false"
              size="small"
              color="nocolor"
              variant="noborder"
              :fallback-action="openFreemiumModal"
              @click="openUpdateDialog(alert)"
            >
              <i class="fa-solid fa-pen edit-icon"></i>
            </ButtonWithPaywall>

            <ButtonWithPaywall
              rounded
              :overrideIcon="false"
              unstyled
              size="small"
              color="nocolor"
              variant="noborder"
              :fallback-action="openFreemiumModal"
              @click="openDeleteDialog(alert)"
            >
              <i class="fa-solid fa-trash-can delete-icon"></i>
            </ButtonWithPaywall>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
  <NoAlert
    v-else
    title="Aucune alerte configurée"
    message="Configurez une alerte pour être averti en temps réel des nouveaux
        éléments pertinents disponibles."
  />

  <PopupDialog ref="updateModal" title="Éditer une alerte" @close="closeDialog">
    <template #body>
      <AddEditAlertForm
        v-if="showForm"
        ref="updateForm"
        :targeted-alert="targetedAlert"
      />
    </template>
    <template #footer>
      <button v-if="showForm" v-focus class="btn-primary" @click="updateAlert">
        Éditer
      </button>
    </template>
  </PopupDialog>
  <PopupDialog
    ref="deleteModal"
    title="Supprimer une alerte"
    @close="targetedAlert = null"
  >
    <template #body>
      <p class="modal-text">Êtes-vous sûr de vouloir supprimer l'alerte</p>
      <p class="modal-text">"{{ targetedAlert?.name }}" ?</p>
    </template>
    <template #footer>
      <button v-focus class="btn-delete" @click="deleteAlert">Supprimer</button>
    </template>
  </PopupDialog>
</template>

<script setup>
import { inject, ref } from "vue";
import SwitchSlide from "@follaw-fr/components/reusable/atoms/SwitchSlide.vue";
import PopupDialog from "@follaw-fr/components/reusable/atoms/PopupDialog.vue";
import ServiceTooltip from "@follaw-fr/components/alerting/atoms/ServiceTooltip.vue";
import NoAlert from "@follaw-fr/components/alerting/atoms/NoAlert.vue";
import AddEditAlertForm from "@follaw-fr/components/alerting/molecules/AddEditAlertForm.vue";
import { useAlertsStore } from "@follaw-fr/stores/alerts";
import { openAlertPaywallModalKey } from "@follaw-fr/constants/injection-keys";
import ButtonWithPaywall from "@follaw-fr/components/reusable/molecules/ButtonWithPaywall.vue";
import { usePermissionGuard } from "@follaw-fr/composables/usePermissionGuard";

const openFreemiumModal = inject(openAlertPaywallModalKey) ?? undefined;
const store = useAlertsStore();

const { permissionGuard } = usePermissionGuard();

const handleOnClick = (e) =>
  permissionGuard({
    fallBack: () => {
      e.preventDefault();
      openFreemiumModal();
    },
  });

const services = ref([
  {
    name: "event",
    displayName: "Événements",
    icon: "fa-solid fa-calendar-lines",
  },
  { name: "jorf", displayName: "Jorf", icon: "fa-kit fa-jorf" },
  // {
  //   name: "petition",
  //   displayName: "Pétitions",
  //   icon: "fa-solid fa-file-signature",
  // },
  { name: "news", displayName: "Actualités", icon: "fa-kit fa-communiques" },
  {
    name: "legislation",
    displayName: "Dossiers législatifs",
    icon: "fa-kit fa-dossierlegi",
  },
  { name: "amendment", displayName: "Amendements", icon: "fa-kit fa-am" },
  { name: "question", displayName: "Questions", icon: "fa-kit fa-questions" },
  { name: "compte-rendu", displayName: "Comptes-rendus", icon: "fa-kit fa-cr" },
  {
    name: "social-media",
    displayName: "Publications",
    icon: "fa-solid fa-chart-network",
  },
  // {
  //   name: "articles",
  //   displayName: "Articles",
  //   icon: "fa-kit fa-sv-articles",
  // },
  {
    name: "transcription",
    displayName: "Transcriptions",
    icon: "fa-solid fa-file-audio",
  },
]);

const targetedAlert = ref(null);

const deleteModal = ref();
const openDeleteDialog = (alert) => {
  targetedAlert.value = alert;
  deleteModal.value?.open();
};
const deleteAlert = () => {
  store.deleteAlert(targetedAlert.value._id);
  deleteModal.value?.close();
};

const updateModal = ref();
const showForm = ref(false);
const openUpdateDialog = (alert) => {
  targetedAlert.value = alert;
  showForm.value = true;
  updateModal.value?.open();
};

const updateForm = ref();
const updateAlert = () => {
  if (updateForm.value?.validate()) {
    showForm.value = false;
    updateForm.value?.form.requestSubmit();
  }
};

const closeDialog = () => {
  targetedAlert.value = null;
  showForm.value = false;
};
</script>

<style lang="scss" scoped>
.alerts-table {
  thead {
    color: var(--clr-n8);
    background-color: var(--clr-p2);

    th {
      &.center {
        text-align: center;
      }

      &.state {
        width: 75px;
      }

      &.actions {
        width: 85px;
      }
    }
  }

  tbody {
    tr {
      &:nth-child(even) {
        background-color: var(--clr-n1);
      }

      &:hover {
        background-color: var(--clr-p1);
      }

      td {
        .center {
          display: flex;
          justify-content: space-around;
        }

        .services {
          display: flex;
          gap: 3px;
          font-weight: 400;

          .service-icon {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            background-color: var(--clr-p2);

            &.active {
              background-color: var(--clr-p5);
            }
          }
        }

        .actions {
          gap: 5px;
        }
      }
    }
  }

  .fa-envelope {
    color: var(--clr-p5);
  }
  .fa-telegram {
    color: var(--clr-telegram);
  }
  .fa-slack {
    color: var(--clr-slack);
  }
  .fa-ms-teams {
    color: var(--clr-ms-teams);
  }

  .fa-crown {
    color: var(--clr-s6);
  }
}

.edit-icon {
  color: var(--clr-s7);
}

.delete-icon {
  color: var(--clr-e300);
}

// Modal contents
.modal-text {
  margin: 0;
  text-align: center;
}
</style>
